import pickBy from 'lodash/pickBy';

import {COMP_ARRAYS, COMP_FIELDS} from '@/constants/comp';
import {PRV_COMP_ARRAYS, PRV_COMP_FIELDS} from '@/constants/prv-comp';
import {DocumentData} from '@/engine';
import {logger} from '@/initializers/logger';
import {Booleans} from '@/model/src/values/dv-boolean';
import {DomainValueReference} from '@/model/types';
import {
  DetailedInvestorAccount,
  AccountBeneficiary,
  AccountInvestments,
  AccountPac,
  BenevaGuaranteedInterestAccount,
  BenevaGuaranteedInvestmentFund,
  BenevaInvestmentAccount,
  DailyInterestAccount,
  HighYieldAccount,
  PacInvestmentInstruction,
  PacSimpleInvestmentInstruction,
} from '@/types/client-info';
import {
  mapAccountFabToDomainValue,
  mapAccountPurchaserMemberContractholderToDomainValue,
  mapAccountSpousalFlagToDomainValue,
  mapAccountTypeToDomainValue,
  mapDateValue,
  mapGuaranteeToDomainValue,
  mapGuaranteedInvestmentFundFeeTypeToDomainValue,
  mapGuaranteedInvestmentFundNameCodeToDomainValue,
  mapInvestmentAccountFeeTypeToDomainValue,
  mapInvestmentAccountFundNameCodeToDomainValue,
  mapPacInvestmentAccountFeeTypeToDomainValue,
  mapPeriodicDepositFrequencyToDomainValue,
  mapPeriodicDepositStatusToDomainValue,
  mapSplitOptionToDomainValue,
  mapProductTypeToDomainValue,
  mapRedeemableFlagToDomainValue,
} from '@/utils/client-info/mappers';

type AccountConstantsPrefix = 'COMP' | 'PRV_COMP';

type COMPFieldKey = `${AccountConstantsPrefix}${string}`;
type COMPArrayFieldKey = `${AccountConstantsPrefix}${string}#${number}`;

type MappedAccount = Record<COMPFieldKey | COMPArrayFieldKey, DocumentData['data'][string] | undefined>;

type CompFields = typeof COMP_FIELDS | typeof PRV_COMP_FIELDS;
type CompArrays = typeof COMP_ARRAYS | typeof PRV_COMP_ARRAYS;

const toStringValue = (value: number | string | null | undefined): string | undefined =>
  value === null || value === undefined ? undefined : value.toString();

const toBooleanDomainValue = (value: boolean | null | undefined) =>
  value === null || value === undefined ? undefined : {$ref: value ? Booleans.True : Booleans.False};

const mapArrayCount = (compCountField: COMPFieldKey, arrayName: string, length: number): MappedAccount => ({
  [`_${arrayName}[count]`]: toStringValue(length),
  [compCountField]: {$ref: `_${arrayName}[count]`},
});

const getTotalInvestmentsCount = (investments: AccountInvestments | null): number =>
  investments === null
    ? 0
    : investments.benevaGuaranteedInvestmentFunds.length +
      investments.benevaInvestmentAccounts.length +
      investments.benevaGuaranteedInterestAccounts.length +
      investments.dailyInterestAccounts.length +
      investments.highYieldAccounts.length;

const mapBenevaGuaranteedInvestmentFunds = (
  compFields: CompFields,
  compArrays: CompArrays,
  funds: BenevaGuaranteedInvestmentFund[],
): MappedAccount =>
  funds.reduce<MappedAccount>(
    (memo, fund, index) => {
      memo[`${compFields.COMP8_benevaGuaranteedInvestmentFundCode}#${index}`] = fund.fundCode;
      memo[`${compFields.COMP9_benevaGuaranteedInvestmentFundNameCode}#${index}`] =
        mapGuaranteedInvestmentFundNameCodeToDomainValue(fund.fundNameCode) ?? undefined;
      memo[`${compFields.COMP10_benevaGuaranteedInvestmentFundFeeType}#${index}`] =
        mapGuaranteedInvestmentFundFeeTypeToDomainValue(fund.feeType) ?? undefined;
      memo[`${compFields.COMP11_benevaGuaranteedInvestmentFundBalance}#${index}`] = toStringValue(fund.balance);
      return memo;
    },
    mapArrayCount(
      compFields.COMP7_benevaGuaranteedInvestmentFundsCount,
      compArrays.COMP7_benevaGuaranteedInvestmentFunds,
      funds.length,
    ),
  );

const mapBenevaInvestmentAccounts = (
  compFields: CompFields,
  compArrays: CompArrays,
  accounts: BenevaInvestmentAccount[],
): MappedAccount =>
  accounts.reduce<MappedAccount>(
    (memo, account, index) => {
      memo[`${compFields.COMP13_benevaInvestmentAccountFundCode}#${index}`] = account.fundCode;
      memo[`${compFields.COMP14_benevaInvestmentAccountFundNameCode}#${index}`] =
        mapInvestmentAccountFundNameCodeToDomainValue(account.fundNameCode) ?? undefined;
      memo[`${compFields.COMP15_benevaInvestmentAccountFeeType}#${index}`] =
        mapInvestmentAccountFeeTypeToDomainValue(account.feeType) ?? undefined;
      memo[`${compFields.COMP16_benevaInvestmentAccountBalance}#${index}`] = toStringValue(account.balance);
      return memo;
    },
    mapArrayCount(
      compFields.COMP12_benevaInvestmentAccountsCount,
      compArrays.COMP12_benevaInvestmentAccounts,
      accounts.length,
    ),
  );

const mapDailyInterestAccounts = (
  compFields: CompFields,
  compArrays: CompArrays,
  accounts: DailyInterestAccount[],
): MappedAccount =>
  accounts.reduce<MappedAccount>(
    (memo, account, index) => {
      memo[`${compFields.COMP18_dailyInterestAccountFundCode}#${index}`] = account.fundCode;
      memo[`${compFields.COMP19_dailyInterestAccountBalance}#${index}`] = toStringValue(account.balance);
      return memo;
    },
    mapArrayCount(
      compFields.COMP17_dailyInterestAccountsCount,
      compArrays.COMP17_dailyInterestAccounts,
      accounts.length,
    ),
  );

const mapHighYieldAccounts = (
  compFields: CompFields,
  compArrays: CompArrays,
  accounts: HighYieldAccount[],
): MappedAccount =>
  accounts.reduce<MappedAccount>(
    (memo, account, index) => {
      memo[`${compFields.COMP21_highYieldAccountFundCode}#${index}`] = account.fundCode;
      memo[`${compFields.COMP22_highYieldAccountBalance}#${index}`] = toStringValue(account.balance);
      return memo;
    },
    mapArrayCount(compFields.COMP20_highYieldAccountsCount, compArrays.COMP20_highYieldAccounts, accounts.length),
  );

const mapGuaranteedInterestAccounts = (
  compFields: CompFields,
  compArrays: CompArrays,
  accounts: BenevaGuaranteedInterestAccount[],
): MappedAccount =>
  accounts.reduce<MappedAccount>(
    (memo, account, index) => {
      memo[`${compFields.COMP24_guaranteedInterestAccountFundCode}#${index}`] = account.fundCode;
      memo[`${compFields.COMP25_guaranteedInterestAccountCertificateNo}#${index}`] = account.certificateNo;
      memo[`${compFields.COMP26_guaranteedInterestAccountRedeemableFlag}#${index}`] =
        mapRedeemableFlagToDomainValue(account.redeemableFlag) ?? undefined;
      memo[`${compFields.COMP27_guaranteedInterestAccountMaturityDate}#${index}`] = mapDateValue(account.maturityDate);
      memo[`${compFields.COMP28_guaranteedInterestAccountBalance}#${index}`] = toStringValue(account.balance);
      memo[`${compFields.COMP29_guaranteedInterestAccountIsWaitingIssueFlag}#${index}`] = toBooleanDomainValue(
        account.isWaitingIssueFlag,
      );
      return memo;
    },
    mapArrayCount(
      compFields.COMP23_guaranteedInterestAccountsCount,
      compArrays.COMP23_guaranteedInterestAccounts,
      accounts.length,
    ),
  );

const mapBeneficiaries = (
  compFields: CompFields,
  compArrays: CompArrays,
  beneficiaries: AccountBeneficiary[],
): MappedAccount =>
  beneficiaries.reduce<MappedAccount>(
    (memo, beneficiary, index) => {
      memo[`${compFields.COMP31_beneficiaryFirstName}#${index}`] = beneficiary.firstName;
      memo[`${compFields.COMP32_beneficiaryLastName}#${index}`] = beneficiary.lastName;
      memo[`${compFields.COMP33_beneficiaryRevocableFlag}#${index}`] = toBooleanDomainValue(beneficiary.revocableFlag);
      return memo;
    },
    mapArrayCount(compFields.COMP30_beneficiariesCount, compArrays.COMP30_beneficiaries, beneficiaries.length),
  );

const mapPacInvestmentInstructions = (
  instructions: PacInvestmentInstruction[],
  arrayName: string,
  fields: {
    count: COMPFieldKey;
    fundCode: COMPFieldKey;
    fundNameCode: COMPFieldKey;
    feeType: COMPFieldKey;
    amount: COMPFieldKey;
  },
  mappers: {
    feeType: (feeType?: string) => DomainValueReference | null;
    fundNameCode: (fundNameCode?: string) => DomainValueReference | null;
  },
): MappedAccount =>
  instructions.reduce<MappedAccount>(
    (memo, instruction, index) => {
      memo[`${fields.fundCode}#${index}`] = instruction.fundCode;
      memo[`${fields.fundNameCode}#${index}`] = mappers.fundNameCode(instruction.fundNameCode) ?? undefined;
      memo[`${fields.feeType}#${index}`] = mappers.feeType(instruction.feeType) ?? undefined;
      memo[`${fields.amount}#${index}`] = toStringValue(instruction.dollarPercentageAmount);
      return memo;
    },
    mapArrayCount(fields.count, arrayName, instructions.length),
  );

const mapPacSimpleInvestmentInstructions = (
  instructions: PacSimpleInvestmentInstruction[],
  arrayName: string,
  fields: {count: COMPFieldKey; fundCode: COMPFieldKey; amount: COMPFieldKey},
): MappedAccount =>
  instructions.reduce<MappedAccount>(
    (memo, instruction, index) => {
      memo[`${fields.fundCode}#${index}`] = instruction.fundCode;
      memo[`${fields.amount}#${index}`] = toStringValue(instruction.dollarPercentageAmount);
      return memo;
    },
    mapArrayCount(fields.count, arrayName, instructions.length),
  );

const mapPac = (compFields: CompFields, compArrays: CompArrays, pacs: AccountPac[]): MappedAccount => {
  const pacCount: MappedAccount = {
    ...mapArrayCount(compFields.COMP34_pacCount, compArrays.COMP34_pac, pacs.length),
    [compFields.COMP62_hasMultiplePacs]: toBooleanDomainValue(pacs.length > 1),
  };

  if (pacs.length !== 1) {
    if (pacs.length > 1) {
      logger.error(`Expected at most one PAC per account, received ${pacs.length}. Skipping the PAC mapping.`);
    }

    return pacCount;
  }

  const [pac] = pacs;
  const instructions = pac.investmentInstructions;

  return {
    ...pacCount,
    [compFields.COMP35_pacAmount]: toStringValue(pac.periodicDepositAmount),
    [compFields.COMP36_pacDepositFrequency]:
      mapPeriodicDepositFrequencyToDomainValue(pac.depositFrequency) ?? undefined,
    [compFields.COMP37_pacStatus]: mapPeriodicDepositStatusToDomainValue(pac.statusCode) ?? undefined,
    [compFields.COMP38_pacSplitOption]: mapSplitOptionToDomainValue(pac.splitOption) ?? undefined,
    [compFields.COMP39_pacCount]: toStringValue(1),
    [compFields.COMP40_pacBankAccountHolder]: pac.bankAccount.bankAccountHolder,
    [compFields.COMP41_pacBankAccountBankNo]: pac.bankAccount.bankNo,
    [compFields.COMP42_pacBankAccountTransit]: pac.bankAccount.transit,
    [compFields.COMP43_pacBankAccountFolio]: pac.bankAccount.folio,
    [compFields.COMP44_pacInvestmentInstructionsCount]: toStringValue(
      instructions.benevaInvestmentAccounts.length +
        instructions.benevaGuaranteedInvestmentFunds.length +
        instructions.dailyInterestAccounts.length +
        instructions.highYieldAccounts.length,
    ),
    ...mapPacInvestmentInstructions(
      instructions.benevaInvestmentAccounts,
      compArrays.COMP45_pacBenevaInvestmentAccounts,
      {
        count: compFields.COMP45_pacBenevaInvestmentAccountsCount,
        fundCode: compFields.COMP46_pacBenevaInvestmentAccountFundCode,
        fundNameCode: compFields.COMP60_pacBenevaInvestmentAccountFundNameCode,
        feeType: compFields.COMP47_pacBenevaInvestmentAccountFeeType,
        amount: compFields.COMP48_pacBenevaInvestmentAccountAmount,
      },
      {
        feeType: mapPacInvestmentAccountFeeTypeToDomainValue,
        fundNameCode: mapInvestmentAccountFundNameCodeToDomainValue,
      },
    ),
    ...mapPacInvestmentInstructions(
      instructions.benevaGuaranteedInvestmentFunds,
      compArrays.COMP55_pacBenevaGuaranteedInvestmentFunds,
      {
        count: compFields.COMP55_pacBenevaGuaranteedInvestmentFundsCount,
        fundCode: compFields.COMP56_pacBenevaGuaranteedInvestmentFundFundCode,
        fundNameCode: compFields.COMP61_pacBenevaGuaranteedInvestmentFundNameCode,
        feeType: compFields.COMP57_pacBenevaGuaranteedInvestmentFundFeeType,
        amount: compFields.COMP58_pacBenevaGuaranteedInvestmentFundAmount,
      },
      {
        feeType: mapGuaranteedInvestmentFundFeeTypeToDomainValue,
        fundNameCode: mapGuaranteedInvestmentFundNameCodeToDomainValue,
      },
    ),
    ...mapPacSimpleInvestmentInstructions(
      instructions.dailyInterestAccounts,
      compArrays.COMP49_pacDailyInterestAccounts,
      {
        count: compFields.COMP49_pacDailyInterestAccountsCount,
        fundCode: compFields.COMP50_pacDailyInterestAccountFundCode,
        amount: compFields.COMP51_pacDailyInterestAccountAmount,
      },
    ),
    ...mapPacSimpleInvestmentInstructions(instructions.highYieldAccounts, compArrays.COMP52_pacHighYieldAccounts, {
      count: compFields.COMP52_pacHighYieldAccountsCount,
      fundCode: compFields.COMP53_pacHighYieldAccountFundCode,
      amount: compFields.COMP54_pacHighYieldAccountAmount,
    }),
  };
};

export const mapAccountToCOMP = (
  account: DetailedInvestorAccount,
  compFields: CompFields = COMP_FIELDS,
  compArrays: CompArrays = COMP_ARRAYS,
): DocumentData['data'] => {
  const investments = account.investments;

  const mapped: MappedAccount = {
    [compFields.COMP1_accountNo]: toStringValue(account.accountNo),
    [compFields.COMP2_plan]: mapAccountTypeToDomainValue(account.plan) ?? undefined,
    [compFields.COMP3_guarantee]: mapGuaranteeToDomainValue(account.guaranty) ?? undefined,
    [compFields.COMP4_productType]: mapProductTypeToDomainValue(account.productType) ?? undefined,
    [compFields.COMP5_balance]: toStringValue(account.balance ?? 0),
    [compFields.COMP6_totalInvestmentsCount]: toStringValue(getTotalInvestmentsCount(investments)),
    [compFields.COMP59_payrollDeduction]: toBooleanDomainValue(account.payrollDeductionFlag),
    [compFields.COMP63_spousalFlag]: mapAccountSpousalFlagToDomainValue(account.spousalFlag) ?? undefined,
    [compFields.COMP64_purchaserMemberContractholder]:
      mapAccountPurchaserMemberContractholderToDomainValue(account.purchaserMemberContractHolder) ?? undefined,
    [compFields.COMP65_fab]: mapAccountFabToDomainValue(account.fab) ?? undefined,
    ...mapBenevaGuaranteedInvestmentFunds(compFields, compArrays, investments?.benevaGuaranteedInvestmentFunds ?? []),
    ...mapBenevaInvestmentAccounts(compFields, compArrays, investments?.benevaInvestmentAccounts ?? []),
    ...mapDailyInterestAccounts(compFields, compArrays, investments?.dailyInterestAccounts ?? []),
    ...mapHighYieldAccounts(compFields, compArrays, investments?.highYieldAccounts ?? []),
    ...mapGuaranteedInterestAccounts(compFields, compArrays, investments?.benevaGuaranteedInterestAccounts ?? []),
    ...mapBeneficiaries(compFields, compArrays, account.beneficiaries ?? []),
    ...mapPac(compFields, compArrays, account.pac ?? []),
  };

  return pickBy(mapped, value => value !== undefined && value !== null) as DocumentData['data'];
};
